import { FormGroup } from '@angular/forms';
import { CashoutDraft } from './cashout-currency.types';
import { buildCashoutConfirmationDetails } from './cashout-confirmation-details';
import {
  extractBankCode,
  extractBankName,
  toTitleCase,
} from './cashout-bank.helpers';
import {
  buildBitsoMxnWithdrawalPayload,
  setDefaultBitsoMxBank,
  updateBitsoMxnValidators,
} from './MXN/mxn-form.logic';
import {
  isCopBitsoBankSelected,
  isCopMinteoBankSelected,
  mapCopLocalAccountTypeToMinteo,
  updateCopBitsoValidators,
  updateCopMinteoValidators,
} from './COP/cop-form.logic';
import { getFallbackCashoutTransactionType } from './cashout-transaction-type.helpers';
import {
  buildClearJunctionPayeeBodyForAdd,
  readClearJunctionAddressFields,
} from './EUR/eur-form.logic';
import {
  buildAedFusePayoutPayload,
  updateAedFusePayoutValidators,
} from './AED/aed-form.logic';
import {
  buildUsdFusePayoutPayload,
  updateUsdFusePayoutValidators,
} from './USD/usd-form.logic';
import {
  buildNotbankBankAccountPayload,
  isNotbankBankSelected,
  updateNotbankBankValidators,
} from './notbank-cashout.logic';

/**
 * Cada builder devuelve el body exacto que se enviará al backend junto con las
 * filas de confirmación. El padre solo recibe el draft, muestra el modal y hace
 * el submit final.
 */
export function buildVesCashoutDraft(ctx: any): CashoutDraft | null {
  const draft = buildDefaultCashoutDraft(ctx, 'VES');
  if (!draft) {
    return null;
  }
  const row = Array.isArray(draft.body) ? draft.body[0] : draft.body;
  if (!row || !ctx.isImmediateCreditType?.(row.transaction_type)) {
    return draft;
  }

  const idType = String(row.id_type || '').trim();
  const idNumber = String(row.id_number || '').trim();
  if (!idType || !idNumber) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Documento',
      detail: 'Indica el tipo y el número de documento del beneficiario.',
    });
    return null;
  }

  const concepto = String(row.description || '').trim();
  if (!concepto || concepto.length > 30) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Concepto',
      detail: 'El concepto del crédito inmediato debe tener entre 1 y 30 caracteres.',
    });
    return null;
  }

  const destination = ctx.r4CreditDestination === 'account' ? 'account' : 'phone';
  const accountNumber = String(row.account_number || '').replace(/\D/g, '');
  if (destination === 'account' && !/^\d{20}$/.test(accountNumber)) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Cuenta',
      detail: 'La cuenta del crédito inmediato debe tener 20 dígitos.',
    });
    return null;
  }
  if (destination === 'phone' && !/^\d{11}$/.test(accountNumber)) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Teléfono',
      detail: 'El teléfono del crédito inmediato debe tener 11 dígitos.',
    });
    return null;
  }

  row.r4_immediate_credit = true;
  row.concepto = concepto;
  row.r4_destination = destination;
  row.provider_service_id = ctx.r4CashoutProviderServiceId;
  row.banco = row.company_id;
  if (destination === 'account') {
    row.cuenta = accountNumber;
  } else {
    row.telefono = accountNumber;
  }
  draft.body = [row];
  draft.confirmationDetails = buildCashoutConfirmationDetails(ctx, draft.body);
  return draft;
}

export function buildEurCashoutDraft(ctx: any): CashoutDraft | null {
  return buildDefaultCashoutDraft(ctx, 'EUR');
}

export function buildMxnCashoutDraft(ctx: any): CashoutDraft | null {
  // MXN requiere payload Bitso SPEI además del body estándar de cashout.
  updateBitsoMxnValidators(ctx);
  const mx = ctx.newTransactionForm.get('bitso_mxn') as FormGroup;
  mx.markAllAsTouched();
  ctx.newTransactionForm.markAllAsTouched();

  if (mx.invalid || ctx.newTransactionForm.controls.amount.invalid) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Datos incompletos',
      detail:
        'Completa nombre, apellidos, monto, protocolo SPEI, CLABE/tarjeta/teléfono e institución si aplica.',
    });
    return null;
  }

  setDefaultBitsoMxBank(ctx);

  const selectedBank = ctx.newTransactionForm.controls.company_id.value;
  const bankCode = extractBankCode(selectedBank);
  const bankName =
    (ctx.newTransactionForm.controls.company_name.value ?? '')
      .toString()
      .trim() ||
    extractBankName(selectedBank) ||
    'BITSO MXN SPEI';

  if (!bankCode) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Banco',
      detail: 'No se pudo fijar el proveedor BITSO_MXN para MXN.',
    });
    return null;
  }

  const bitsoPayload = buildBitsoMxnWithdrawalPayload(ctx);
  if (!bitsoPayload) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Datos inválidos',
      detail: 'Verifica beneficiario (nombre y apellidos) y monto.',
    });
    return null;
  }

  const mxv = mx.getRawValue() as Record<string, string>;
  const clientId = normalizeClientId(
    ctx.newTransactionForm.controls.client_id.value,
  );
  const clabe = (mxv['clabe'] ?? '').toString().trim();

  const base: Record<string, unknown> = {
    id: ctx.newTransactionForm.controls.transaction_id.value?.toString(),
    time_zone: ctx.newTransactionForm.controls.time_zone.value,
    firstname: ctx.newTransactionForm.controls.firstname.value,
    lastname: ctx.newTransactionForm.controls.lastname.value,
    id_type: 'RFC',
    id_number: (mxv['rfc'] ?? '').toString().trim() || '-',
    transaction_type: ctx.newTransactionForm.controls.transaction_type.value,
    company_name: bankName,
    company_id: bankCode,
    account_type: 'SPEI',
    account_number: clabe,
    amount: ctx.newTransactionForm.controls.amount.value!.toString(),
    description:
      (ctx.newTransactionForm.controls.description?.value ?? '')
        .toString()
        .trim() || undefined,
    client_id: clientId,
    currency: 'MXN',
    type: 'cashout',
    bitso_mxn_payload: bitsoPayload,
  };

  return toDraft(ctx, [base]);
}

export function buildCopCashoutDraft(ctx: any): CashoutDraft | null {
  // COP: Bitso (BITSO_COP), Minteo (canal/BreB), Notbank o banco tradicional.
  updateCopMinteoValidators(ctx);
  updateCopBitsoValidators(ctx);
  if (isNotbankBankSelected(ctx)) {
    const nb = ctx.newTransactionForm.get('notbank_bank') as FormGroup;
    if (nb) {
      updateNotbankBankValidators(nb, 'COP');
    }
  }
  ctx.newTransactionForm.markAllAsTouched();
  if (ctx.newTransactionForm.invalid) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Datos incompletos',
      detail:
        'Revisa proveedor, documento, banco beneficiario / cuenta o canal Minteo según corresponda.',
    });
    return null;
  }

  const docValue = (
    ctx.newTransactionForm.controls.document_type_number.value ?? ''
  )
    .toString()
    .trim();
  if (!docValue) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Documento requerido',
      detail:
        'Ingresa el documento del beneficiario en formato tipo-número (ej. CC-123456789).',
    });
    return null;
  }
  const parts = docValue.split('-');
  if (parts.length < 2 || !parts[0].trim() || !parts[1].trim()) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Formato de documento incorrecto',
      detail: 'Usa el formato tipo-número con un guion (ej. CC-123456789).',
    });
    return null;
  }

  const idType = parts[0].trim();
  const idNumber = parts.slice(1).join('-').trim();
  const clientId = normalizeClientId(
    ctx.newTransactionForm.controls.client_id.value,
  );
  if (isCopBitsoBankSelected(ctx)) {
    return buildCopBitsoDraft(ctx, idType, idNumber, clientId);
  }

  if (isNotbankBankSelected(ctx)) {
    return buildCopNotbankDraft(ctx, idType, idNumber, clientId);
  }

  const isMinteoBank = isCopMinteoBankSelected(ctx);

  if (!isMinteoBank) {
    return buildCopBankDraft(ctx, idType, idNumber, clientId);
  }

  const channel = (
    ctx.newTransactionForm.controls.minteo_channel.value ?? 'SAVE'
  )
    .toString()
    .toUpperCase();

  if (channel === 'BREB') {
    return buildCopBrebDraft(ctx, idType, idNumber, clientId);
  }

  return buildCopMinteoBankDraft(ctx, idType, idNumber, clientId, channel);
}

export function buildLatamNotbankCashoutDraft(ctx: any): CashoutDraft | null {
  const currency = String(ctx.currency || '')
    .trim()
    .toUpperCase();
  const nb = ctx.newTransactionForm.get('notbank_bank') as FormGroup;
  if (nb) {
    updateNotbankBankValidators(nb, currency);
  }
  ctx.newTransactionForm.markAllAsTouched();
  if (ctx.newTransactionForm.invalid) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Datos incompletos',
      detail:
        'Completa beneficiario, monto, proveedor Notbank y cuenta/PIX destino.',
    });
    return null;
  }

  const bankPayload = buildNotbankBankAccountPayload(nb, currency, ctx);
  if (!bankPayload) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Cuenta Notbank',
      detail: 'Completa banco, tipo y número/clave PIX (y extras del país).',
    });
    return null;
  }

  const selectedBank = ctx.newTransactionForm.controls.company_id.value;
  let bankCode = extractBankCode(selectedBank);
  let bankName =
    (ctx.newTransactionForm.controls.company_name.value ?? '')
      .toString()
      .trim() || extractBankName(selectedBank);

  if (!bankCode || !String(bankCode).toUpperCase().includes('NOTBANK')) {
    // Force Notbank company code if user picked wrong / empty
    bankCode = `NOTBANK_${currency}`;
    bankName = bankName || `NOTBANK ${currency}`;
  }

  const clientId = normalizeClientId(
    ctx.newTransactionForm.controls.client_id.value,
  );
  const nbv = nb.getRawValue() as Record<string, any>;
  const body: Record<string, unknown> = {
    id: ctx.newTransactionForm.controls.transaction_id.value?.toString(),
    time_zone: ctx.newTransactionForm.controls.time_zone.value || 'GMT',
    firstname: ctx.newTransactionForm.controls.firstname.value,
    lastname: ctx.newTransactionForm.controls.lastname.value,
    id_type: ctx.newTransactionForm.controls.id_type.value || 'V',
    id_number:
      ctx.newTransactionForm.controls.id_number.value?.toString() ?? '',
    transaction_type:
      ctx.newTransactionForm.controls.transaction_type.value ||
      'Transferencia bancaria',
    company_name: bankName,
    company_id: bankCode,
    account_type: String(bankPayload['kind'] || ''),
    account_number: String(bankPayload['number'] || ''),
    amount: ctx.newTransactionForm.controls.amount.value!.toString(),
    description:
      (ctx.newTransactionForm.controls.description?.value ?? '')
        .toString()
        .trim() || undefined,
    client_id: clientId,
    currency,
    type: 'cashout',
    notbank_bank_account: bankPayload,
    bank_account: bankPayload,
  };
  if (currency === 'ARS') {
    if (nbv['cbu']) body['cbu'] = String(nbv['cbu']).trim();
    if (nbv['cuit']) body['cuit'] = String(nbv['cuit']).trim();
    if (nbv['person_type']) body['person_type'] = String(nbv['person_type']).trim();
  }
  return toDraft(ctx, [body]);
}

function buildCopNotbankDraft(
  ctx: any,
  idType: string,
  idNumber: string,
  clientId: any,
): CashoutDraft | null {
  const nb = ctx.newTransactionForm.get('notbank_bank') as FormGroup;
  const bankPayload = buildNotbankBankAccountPayload(nb, 'COP', ctx);
  if (!bankPayload) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Cuenta Notbank',
      detail: 'Completa banco, tipo (corriente/ahorro) y número de cuenta.',
    });
    return null;
  }
  const selectedBank = ctx.newTransactionForm.controls.company_id.value;
  const bankCode = extractBankCode(selectedBank) || 'NOTBANK_COP';
  const bankName =
    extractBankName(selectedBank) ||
    ctx.newTransactionForm.controls.company_name.value ||
    'NOTBANK COP';
  const body: Record<string, unknown> = {
    id: ctx.newTransactionForm.controls.transaction_id.value?.toString(),
    time_zone: ctx.newTransactionForm.controls.time_zone.value,
    firstname: ctx.newTransactionForm.controls.firstname.value,
    lastname: ctx.newTransactionForm.controls.lastname.value,
    id_type: idType,
    id_number: idNumber,
    transaction_type: ctx.newTransactionForm.controls.transaction_type.value,
    company_name: bankName,
    company_id: bankCode,
    account_type: String(bankPayload['kind'] || ''),
    account_number: String(bankPayload['number'] || ''),
    amount: ctx.newTransactionForm.controls.amount.value!.toString(),
    description:
      (ctx.newTransactionForm.controls.description?.value ?? '')
        .toString()
        .trim() || undefined,
    client_id: clientId,
    currency: 'COP',
    type: 'cashout',
    notbank_bank_account: bankPayload,
    bank_account: bankPayload,
  };
  return toDraft(ctx, [body]);
}

export function buildAedCashoutDraft(ctx: any): CashoutDraft | null {
  return buildDefaultCashoutDraft(ctx, 'AED');
}

export function buildUsdCashoutDraft(ctx: any): CashoutDraft | null {
  return buildDefaultCashoutDraft(ctx, 'USD');
}

export function buildBtcCashoutDraft(ctx: any): CashoutDraft | null {
  return buildCryptoCashoutDraft(ctx, 'BTC', 'Bitcoin');
}

export function buildEthCashoutDraft(ctx: any): CashoutDraft | null {
  return buildCryptoCashoutDraft(ctx, 'ETH', 'Ethereum');
}

export function buildUsdtCashoutDraft(ctx: any): CashoutDraft | null {
  return buildCryptoCashoutDraft(ctx, 'USDT', 'TRC20');
}

function buildCryptoCashoutDraft(
  ctx: any,
  currency: 'BTC' | 'ETH' | 'USDT',
  fallbackNetwork: string,
): CashoutDraft | null {
  // La wallet que descuenta saldo se asigna luego en editar cashout
  // usando "Billetera del Remitente"; aquí solo se guarda red y dirección.
  ctx.newTransactionForm.controls.currency.setValue(currency);

  if (!ctx.newTransactionForm.controls.transaction_type.value) {
    ctx.setTransactionType(getFallbackCashoutTransactionType(currency));
  }

  if (!ctx.newTransactionForm.controls.account_type.value) {
    ctx.newTransactionForm.controls.account_type.setValue(fallbackNetwork);
  }

  ctx.newTransactionForm.markAllAsTouched();
  if (ctx.newTransactionForm.invalid) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Datos incompletos',
      detail:
        'Completa cliente, beneficiario, dirección destino, red y monto del cashout crypto.',
    });
    return null;
  }

  const clientId = normalizeClientId(
    ctx.newTransactionForm.controls.client_id.value,
  );
  const body: Record<string, unknown> = {
    id: ctx.newTransactionForm.controls.transaction_id.value?.toString(),
    time_zone: ctx.newTransactionForm.controls.time_zone.value,
    firstname: ctx.newTransactionForm.controls.firstname.value,
    lastname: ctx.newTransactionForm.controls.lastname.value,
    id_type: ctx.newTransactionForm.controls.id_type.value,
    id_number: ctx.newTransactionForm.controls.id_number.value?.toString() ?? '',
    transaction_type: ctx.newTransactionForm.controls.transaction_type.value,
    company_name: '',
    company_id: '',
    account_type: ctx.newTransactionForm.controls.account_type.value,
    account_number: ctx.newTransactionForm.controls.account_number.value,
    amount: ctx.newTransactionForm.controls.amount.value!.toString(),
    description:
      (ctx.newTransactionForm.controls.description?.value ?? '')
        .toString()
        .trim() || undefined,
    client_id: clientId,
    currency,
    type: 'cashout',
  };

  return toDraft(ctx, [body]);
}

function buildDefaultCashoutDraft(
  ctx: any,
  currency: string,
): CashoutDraft | null {
  // VES/EUR/AED/USD/crypto comparten la forma base; luego se agregan campos de proveedor.
  const selectedBank = ctx.newTransactionForm.controls.company_id.value;
  const bankCode = extractBankCode(selectedBank);
  const selectedBankName = extractBankName(selectedBank);
  const bankNameControl =
    ctx.newTransactionForm.controls.company_name.value?.trim() ?? '';
  const bankName = bankNameControl.length ? bankNameControl : selectedBankName;

  if (!bankCode) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Banco inválido',
      detail:
        'No pudimos identificar el código del banco seleccionado. Verifica la opción e intenta de nuevo.',
    });
    return null;
  }

  if (!bankName) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Banco inválido',
      detail:
        'No pudimos identificar el nombre del banco seleccionado. Verifica la opción e intenta de nuevo.',
    });
    return null;
  }

  const normalizedBankName = bankNameControl.length
    ? toTitleCase(bankName)
    : selectedBankName;
  ctx.newTransactionForm.controls.company_name.setValue(normalizedBankName);

  const clientId = normalizeClientId(
    ctx.newTransactionForm.controls.client_id.value,
  );

  const base: Record<string, unknown> = {
    id: ctx.newTransactionForm.controls.transaction_id.value?.toString(),
    time_zone: ctx.newTransactionForm.controls.time_zone.value,
    firstname: ctx.newTransactionForm.controls.firstname.value,
    lastname: ctx.newTransactionForm.controls.lastname.value,
    id_type: ctx.newTransactionForm.controls.id_type.value,
    id_number: ctx.newTransactionForm.controls.id_number.value?.toString() ?? '',
    transaction_type: ctx.newTransactionForm.controls.transaction_type.value,
    company_name: normalizedBankName,
    company_id: bankCode,
    account_type: ctx.newTransactionForm.controls.account_type.value,
    account_number: ctx.newTransactionForm.controls.account_number.value,
    amount: ctx.newTransactionForm.controls.amount.value!.toString(),
    description:
      (ctx.newTransactionForm.controls.description?.value ?? '')
        .toString()
        .trim() || undefined,
    client_id: clientId,
    currency: ctx.newTransactionForm.controls.currency.value ?? currency,
    type: 'cashout',
  };

  if (currency === 'AED' || currency === 'USD') {
    // Fuse/Lorum espera payload anidado y monto en unidades menores.
    const fuseGroup = ctx.newTransactionForm.get('fuse_payout') as FormGroup;
    if (currency === 'AED') {
      updateAedFusePayoutValidators(ctx);
    } else {
      updateUsdFusePayoutValidators(ctx);
    }
    if (fuseGroup.invalid) {
      fuseGroup.markAllAsTouched();
      ctx.messageService.add({
        severity: 'error',
        summary: 'Datos incompletos (Fuse / Lorum)',
        detail:
          'Completa cuenta Fuse, propósito, dirección del beneficiario e IBAN o datos wire (y BIC para USD).',
      });
      return null;
    }
    const fusePayload =
      currency === 'AED'
        ? buildAedFusePayoutPayload(ctx)
        : buildUsdFusePayoutPayload(ctx);
    if (!fusePayload) {
      ctx.messageService.add({
        severity: 'error',
        summary: 'Monto inválido',
        detail: 'Ingresa un monto numérico válido para el cashout.',
      });
      return null;
    }
    const fp = fuseGroup.getRawValue();
    base['account_number'] =
      fp.rail === 'iban'
        ? (fp.iban ?? '').toString().trim()
        : (fp.wire_account ?? '').toString().trim();
    base['fuse_payout_payload'] = fusePayload;
  }

  if (currency === 'EUR') {
    // ClearJunction necesita datos de payee/dirección aunque no se muestren
    // como controles bancarios tradicionales.
    const cjBody = buildClearJunctionPayeeBodyForAdd(ctx);
    if (cjBody) {
      base['clearjunction_payee_type'] = cjBody.clearjunction_payee_type;
      base['clearjunction_payee'] = cjBody.clearjunction_payee;
    }
    const addr = readClearJunctionAddressFields(ctx);
    if (addr.country) base['address_country'] = addr.country;
    if (addr.zip) base['address_zip'] = addr.zip;
    if (addr.city) base['address_city'] = addr.city;
    if (addr.street) base['address_street'] = addr.street;
  }

  return toDraft(ctx, [base]);
}

function buildCopBitsoDraft(
  ctx: any,
  idType: string,
  idNumber: string,
  clientId: any,
): CashoutDraft | null {
  const benBank = resolveCopBeneficiaryBank(ctx);
  if (!benBank) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Banco beneficiario requerido',
      detail: 'Selecciona el banco colombiano del beneficiario para el payout Bitso.',
    });
    return null;
  }

  const base: Record<string, unknown> = {
    ...buildCopBase(ctx, idType, idNumber, clientId),
    company_name: 'BITSO COP',
    company_id: 'BITSO_COP',
    bank_code: benBank.bankCode,
    beneficiary_bank_code: benBank.bankCode,
    account_type: ctx.newTransactionForm.controls.account_type.value,
    account_number: ctx.newTransactionForm.controls.account_number.value,
  };

  return toDraft(ctx, [base]);
}

function resolveCopBeneficiaryBank(ctx: any): {
  bankCode: string;
  normalizedBankName: string;
} | null {
  const selected = ctx.newTransactionForm.controls.cop_beneficiary_bank.value;
  const bankCode = extractBankCode(selected);
  const bankName = extractBankName(selected);
  if (!bankCode) {
    return null;
  }
  return {
    bankCode,
    normalizedBankName: bankName || bankCode,
  };
}

function buildCopBankDraft(
  ctx: any,
  idType: string,
  idNumber: string,
  clientId: any,
): CashoutDraft | null {
  const bankData = resolveSelectedBank(ctx);
  if (!bankData) return null;

  const base: Record<string, unknown> = {
    ...buildCopBase(ctx, idType, idNumber, clientId),
    company_name: bankData.normalizedBankName,
    company_id: bankData.bankCode,
    account_type: ctx.newTransactionForm.controls.account_type.value,
    account_number: ctx.newTransactionForm.controls.account_number.value,
  };

  return toDraft(ctx, [base]);
}

function buildCopBrebDraft(
  ctx: any,
  idType: string,
  idNumber: string,
  clientId: any,
): CashoutDraft | null {
  // BREB no usa banco/cuenta normal en channel_data; el valor de la llave
  // viaja como account_number para mantener compatibilidad con add_cashouts.
  const keyType = (ctx.newTransactionForm.controls.breb_key_type.value ?? '')
    .toString()
    .trim()
    .toUpperCase();
  const keyValue = (ctx.newTransactionForm.controls.breb_key_value.value ?? '')
    .toString()
    .trim();

  if (!keyType || !keyValue) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Llave BreB incompleta',
      detail:
        'Selecciona el tipo de llave e ingresa el valor (según doc Minteo BREB).',
    });
    return null;
  }

  const base: Record<string, unknown> = {
    ...buildCopBase(ctx, idType, idNumber, clientId),
    company_name: 'Bancolombia',
    company_id: 'BANCOLOMBIA',
    account_type: 'SAVINGS',
    account_number: keyValue,
    minteo_channel: 'BREB',
    minteo_channel_data: { key_type: keyType, key_value: keyValue },
  };
  addCopWalletId(ctx, base);

  return toDraft(ctx, [base]);
}

function buildCopMinteoBankDraft(
  ctx: any,
  idType: string,
  idNumber: string,
  clientId: any,
  channel: string,
): CashoutDraft | null {
  const bankData = resolveSelectedBank(ctx);
  if (!bankData) return null;

  const base: Record<string, unknown> = {
    ...buildCopBase(ctx, idType, idNumber, clientId),
    company_name: bankData.normalizedBankName,
    company_id: bankData.bankCode,
    account_type: mapCopLocalAccountTypeToMinteo(
      ctx.newTransactionForm.controls.account_type.value,
    ),
    account_number: ctx.newTransactionForm.controls.account_number.value,
    minteo_channel: channel,
  };
  addCopWalletId(ctx, base);

  return toDraft(ctx, [base]);
}

function buildCopBase(
  ctx: any,
  idType: string,
  idNumber: string,
  clientId: any,
): Record<string, unknown> {
  return {
    id: ctx.newTransactionForm.controls.transaction_id.value?.toString(),
    time_zone: ctx.newTransactionForm.controls.time_zone.value,
    firstname: ctx.newTransactionForm.controls.firstname.value,
    lastname: ctx.newTransactionForm.controls.lastname.value,
    id_type: idType,
    id_number: idNumber,
    transaction_type: ctx.newTransactionForm.controls.transaction_type.value,
    amount: ctx.newTransactionForm.controls.amount.value!.toString(),
    description:
      (ctx.newTransactionForm.controls.description?.value ?? '')
        .toString()
        .trim() || undefined,
    client_id: clientId,
    currency: 'COP',
    type: 'cashout',
  };
}

function resolveSelectedBank(ctx: any): {
  bankCode: string;
  normalizedBankName: string;
} | null {
  // Los bancos pueden venir como string, objeto de subwallet o metadata; el
  // helper compartido normaliza código y nombre antes de armar el body.
  const selectedBank = ctx.newTransactionForm.controls.company_id.value;
  const bankCode = extractBankCode(selectedBank);
  const selectedBankName = extractBankName(selectedBank);
  const bankNameControl =
    ctx.newTransactionForm.controls.company_name.value?.trim() ?? '';
  const bankName = bankNameControl.length ? bankNameControl : selectedBankName;

  if (!bankCode) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Banco inválido',
      detail:
        'No pudimos identificar el código del banco seleccionado. Verifica la opción e intenta de nuevo.',
    });
    return null;
  }

  if (!bankName) {
    ctx.messageService.add({
      severity: 'error',
      summary: 'Banco inválido',
      detail:
        'No pudimos identificar el nombre del banco seleccionado. Verifica la opción e intenta de nuevo.',
    });
    return null;
  }

  const normalizedBankName = bankNameControl.length
    ? toTitleCase(bankName)
    : selectedBankName;
  ctx.newTransactionForm.controls.company_name.setValue(normalizedBankName);

  return { bankCode, normalizedBankName };
}

function addCopWalletId(ctx: any, base: Record<string, unknown>): void {
  const walletId = ctx.newTransactionForm.controls.minteo_origin_wallet_id.value;
  if (walletId != null && walletId !== '') {
    base['minteo_origin_wallet_id'] = walletId;
  }
}

function normalizeClientId(clientControlValue: any): any {
  return typeof clientControlValue === 'object' && clientControlValue
    ? (clientControlValue.user_id ?? clientControlValue.id ?? null)
    : clientControlValue;
}

function toDraft(ctx: any, body: any): CashoutDraft {
  return {
    body,
    confirmationDetails: buildCashoutConfirmationDetails(ctx, body),
  };
}
